<template>
  <div class="tag-mgmt">
    <!-- Header -->
    <div class="tag-mgmt__header">
      <div class="tag-mgmt__header-left">
        <div class="tag-mgmt__count">
          <span class="tag-mgmt__count-number">{{ tags.length }}</span>
          <span class="tag-mgmt__count-label">{{ tags.length === 1 ? $t('tag') : $t('tags') }}</span>
        </div>
      </div>
      <div class="tag-mgmt__header-right">
        <label v-if="tags.length > 0" class="tag-mgmt__select-all">
          <input
            type="checkbox"
            v-model="selectAll"
            @change="toggleSelectAll"
            class="tag-mgmt__checkbox"
          />
          <span>{{ $t('Select all') }}</span>
        </label>
        <button
          v-if="selectedTags.length > 0"
          type="button"
          @click.prevent="confirmBulkDelete"
          class="tag-mgmt__bulk-delete"
        >
          <svg viewBox="0 0 20 20" fill="currentColor">
            <path fill-rule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clip-rule="evenodd" />
          </svg>
          {{ $t('Delete') }} ({{ selectedTags.length }})
        </button>
      </div>
    </div>

    <!-- Tags List -->
    <div class="tag-mgmt__body">
      <div v-if="loading" class="tag-mgmt__loading">
        <svg class="tag-mgmt__spinner" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path stroke-linecap="round" stroke-linejoin="round" d="M12 6v6l4 2" />
        </svg>
        {{ $t('Loading tags...') }}
      </div>

      <TransitionGroup v-else-if="tags.length > 0" name="tag-list" tag="div" class="tag-mgmt__list">
        <div
          v-for="tag in tags"
          :key="tag.id"
          class="tag-mgmt__row"
          :class="{ 'tag-mgmt__row--selected': selectedTags.includes(tag.id) }"
        >
          <label class="tag-mgmt__row-left">
            <input
              type="checkbox"
              :value="tag.id"
              v-model="selectedTags"
              class="tag-mgmt__checkbox"
            />
            <svg class="tag-mgmt__tag-icon" viewBox="0 0 20 20" fill="currentColor">
              <path fill-rule="evenodd" d="M17.707 9.293a1 1 0 010 1.414l-7 7a1 1 0 01-1.414 0l-7-7A.997.997 0 012 10V5a3 3 0 013-3h5c.256 0 .512.098.707.293l7 7zM5 6a1 1 0 100-2 1 1 0 000 2z" clip-rule="evenodd" />
            </svg>
            <span class="tag-mgmt__tag-name">{{ tag.name }}</span>
            <span class="tag-mgmt__tag-posts">{{ tag.posts_count }} {{ tag.posts_count === 1 ? $t('post') : $t('posts') }}</span>
          </label>
          <button
            type="button"
            @click.prevent="confirmSingleDelete(tag)"
            class="tag-mgmt__delete-btn"
            :title="$t('Delete tag')"
            v-tippy="{ placement: 'top' }"
          >
            <svg viewBox="0 0 20 20" fill="currentColor">
              <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd" />
            </svg>
          </button>
        </div>
      </TransitionGroup>

      <!-- Empty State -->
      <div v-else class="tag-mgmt__empty">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
          <path stroke-linecap="round" stroke-linejoin="round" d="M9.568 3H5.25A2.25 2.25 0 003 5.25v4.318c0 .597.237 1.17.659 1.591l9.581 9.581c.699.699 1.78.872 2.607.33a18.095 18.095 0 005.223-5.223c.542-.827.369-1.908-.33-2.607L11.16 3.66A2.25 2.25 0 009.568 3z" />
          <path stroke-linecap="round" stroke-linejoin="round" d="M6 6h.008v.008H6V6z" />
        </svg>
        <p>{{ $t('No tags have been created for this board yet.') }}</p>
      </div>
    </div>

    <Dialog :title="$t('Confirm Deletion')" v-model="dialogVisible">
      <div class="px-4 py-4 sm:px-6">
        <p class="text-neutral-800">
          {{ confirmationMessage }}
        </p>
        <div class="flex justify-end items-center mt-4 space-x-2">
          <button type="button" class="bg-white border border-gray-300 text-neutral-800 hover:bg-gray-50 rounded-lg shadow-sm px-4 py-2 text-sm font-medium" @click="dialogVisible = false">{{ $t('Cancel') }}</button>
          <button
            type="button"
            class="bg-red-600 hover:bg-red-700 text-white rounded-lg shadow-sm px-4 py-2 text-sm font-medium"
            @click="executeDelete"
          >
            {{ $t('Delete') }}
          </button>
        </div>
      </div>
    </Dialog>
  </div>
</template>

<script>
import Dialog from "../components/dialog";

export default {
  props: ["board-slug"],
  components: { Dialog },
  data() {
    return {
      tags: [],
      selectedTags: [],
      selectAll: false,
      loading: true,
      dialogVisible: false,
      deleteMode: null,
      tagToDelete: null,
      confirmationMessage: ""
    };
  },
  mounted() {
    this.fetchTags();
  },
  methods: {
    fetchTags() {
      Rails.ajax({
        url: `/boards/${this.boardSlug}/tags`,
        type: "GET",
        dataType: "json",
        beforeSend: () => true,
        success: (data) => {
          this.tags = data;
          this.loading = false;
        },
        error: (data, status) => {
          console.error("Error fetching tags:", data);
          this.loading = false;
        }
      });
    },
    toggleSelectAll() {
      this.selectedTags = this.selectAll ? this.tags.map(t => t.id) : [];
    },
    confirmSingleDelete(tag) {
      this.deleteMode = 'single';
      this.tagToDelete = tag;
      const postText = tag.posts_count === 1 ? '1 post' : `${tag.posts_count} posts`;
      this.confirmationMessage = tag.posts_count > 0
        ? `Are you sure you want to delete the tag "${tag.name}"? It will be removed from ${postText}.`
        : `Are you sure you want to delete the tag "${tag.name}"?`;
      this.dialogVisible = true;
    },
    confirmBulkDelete() {
      this.deleteMode = 'bulk';
      const totalPosts = this.tags
        .filter(t => this.selectedTags.includes(t.id))
        .reduce((sum, t) => sum + t.posts_count, 0);
      const tagCount = this.selectedTags.length;
      const tagText = tagCount === 1 ? '1 tag' : `${tagCount} tags`;
      const postText = totalPosts === 1 ? '1 post' : `${totalPosts} posts`;
      this.confirmationMessage = totalPosts > 0
        ? `Are you sure you want to delete ${tagText}? They will be removed from ${postText}.`
        : `Are you sure you want to delete ${tagText}?`;
      this.dialogVisible = true;
    },
    executeDelete() {
      if (this.deleteMode === 'single') {
        this.deleteSingleTag();
      } else {
        this.bulkDeleteTags();
      }
    },
    deleteSingleTag() {
      Rails.ajax({
        url: `/boards/${this.boardSlug}/tags/${this.tagToDelete.id}`,
        type: "DELETE",
        dataType: "json",
        beforeSend: () => true,
        success: () => {
          this.tags = this.tags.filter(t => t.id !== this.tagToDelete.id);
          this.selectedTags = this.selectedTags.filter(id => id !== this.tagToDelete.id);
          this.dialogVisible = false;
          this.tagToDelete = null;
        },
        error: (data) => {
          console.error("Error deleting tag:", data);
        }
      });
    },
    bulkDeleteTags() {
      const params = new FormData();
      this.selectedTags.forEach(id => params.append('tag_ids[]', id));

      Rails.ajax({
        url: `/boards/${this.boardSlug}/tags/bulk_destroy`,
        type: "DELETE",
        data: params,
        dataType: "json",
        beforeSend: () => true,
        success: () => {
          this.tags = this.tags.filter(t => !this.selectedTags.includes(t.id));
          this.selectedTags = [];
          this.selectAll = false;
          this.dialogVisible = false;
        },
        error: (data) => {
          console.error("Error deleting tags:", data);
        }
      });
    }
  },
  watch: {
    selectedTags() {
      this.selectAll = this.selectedTags.length === this.tags.length && this.tags.length > 0;
    }
  }
};
</script>

<style scoped lang="scss">
.tag-mgmt {
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  overflow: hidden;
}

// Header
.tag-mgmt__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 14px;
  border-bottom: 1px solid #f3f4f6;
  background: #fafafa;
}

.tag-mgmt__header-left {
  display: flex;
  align-items: center;
  gap: 8px;
}

.tag-mgmt__header-right {
  display: flex;
  align-items: center;
  gap: 8px;
}

.tag-mgmt__count {
  display: flex;
  align-items: baseline;
  gap: 4px;
  padding: 4px 10px;
  background: #f3f4f6;
  border-radius: 6px;
}

.tag-mgmt__count-number {
  font-size: 14px;
  font-weight: 600;
  color: #404040;
}

.tag-mgmt__count-label {
  font-size: 12px;
  color: #737373;
}

.tag-mgmt__select-all {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  font-weight: 500;
  color: #737373;
  cursor: pointer;
  padding: 4px 8px;
  border-radius: 6px;
  transition: all 0.15s ease;
  margin: 0;

  &:hover {
    color: #404040;
    background: #f3f4f6;
  }
}

.tag-mgmt__checkbox {
  width: 15px;
  height: 15px;
  border-radius: 4px;
  border: 1.5px solid #d1d5db;
  appearance: none;
  -webkit-appearance: none;
  cursor: pointer;
  position: relative;
  transition: all 0.15s ease;
  flex-shrink: 0;
  background: #fff;

  &:checked {
    background: var(--accent-color, #009be5);
    border-color: var(--accent-color, #009be5);

    &::after {
      content: '';
      position: absolute;
      left: 4px;
      top: 1px;
      width: 5px;
      height: 9px;
      border: solid #fff;
      border-width: 0 2px 2px 0;
      transform: rotate(45deg);
    }
  }

  &:hover {
    border-color: #9ca3af;
  }
}

.tag-mgmt__bulk-delete {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  font-size: 12px;
  font-weight: 500;
  color: #dc2626;
  background: #fff;
  border: 1px solid #fca5a5;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.15s ease;
  white-space: nowrap;

  svg {
    width: 13px;
    height: 13px;
  }

  &:hover {
    background: #fecaca;
    color: #b91c1c;
  }
}

// Body
.tag-mgmt__body {
  min-height: 44px;
  max-height: 320px;
  overflow-y: auto;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: #f9fafb;
  }

  &::-webkit-scrollbar-thumb {
    background: #d1d5db;
    border-radius: 3px;

    &:hover {
      background: #9ca3af;
    }
  }
}

.tag-mgmt__list {
  display: flex;
  flex-direction: column;
}

.tag-mgmt__row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 14px;
  border-bottom: 1px solid #f3f4f6;
  transition: all 0.15s ease;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: #f9fafb;
  }

  &--selected {
    background: #f0f9ff;

    &:hover {
      background: #e0f2fe;
    }
  }
}

.tag-mgmt__row-left {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  min-width: 0;
  cursor: pointer;
  margin: 0;
}

.tag-mgmt__tag-icon {
  width: 12px;
  height: 12px;
  color: #a3a3a3;
  flex-shrink: 0;
}

.tag-mgmt__tag-name {
  font-size: 13px;
  font-weight: 500;
  color: #404040;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.tag-mgmt__tag-posts {
  font-size: 11px;
  color: #a3a3a3;
  white-space: nowrap;
  flex-shrink: 0;
  padding: 2px 6px;
  background: #f3f4f6;
  border-radius: 4px;
}

.tag-mgmt__delete-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  padding: 0;
  border: none;
  background: transparent;
  cursor: pointer;
  color: #d1d5db;
  transition: color 0.15s ease;
  flex-shrink: 0;
  margin-left: 8px;

  svg {
    width: 14px;
    height: 14px;
  }

  &:hover {
    color: #ef4444;
  }
}

// Loading
.tag-mgmt__loading {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 24px 16px;
  font-size: 13px;
  color: #a3a3a3;
}

.tag-mgmt__spinner {
  width: 18px;
  height: 18px;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

// Empty State
.tag-mgmt__empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 24px 16px;
  color: #a3a3a3;

  svg {
    width: 32px;
    height: 32px;
    margin-bottom: 6px;
    opacity: 0.4;
  }

  p {
    margin: 0;
    font-size: 13px;
  }
}

// Transitions
.tag-list-enter-active,
.tag-list-leave-active {
  transition: all 0.2s ease;
}

.tag-list-enter {
  opacity: 0;
  transform: translateX(-8px);
}

.tag-list-leave-to {
  opacity: 0;
  transform: translateX(8px);
}

.tag-list-move {
  transition: transform 0.2s ease;
}
</style>
